import { z } from "zod";
import type { BusinessVertical } from "./vertical";

// ---------------------------------------------------------------------------
// Attestation before test invitations (GRA-174, spec §17 "Spärrar före utskick")
//
// Before invitations can go out the manager confirms that employees have been
// informed about the AI support (EU AI Act transparency). In the public
// sector, co-determination (MBL) must be done too; no current vertical
// requires it, but the kind exists so the rule is one line to switch on.
// Both are stored with who, when and which text version was attested.
// ---------------------------------------------------------------------------

export const ATTESTATION_KINDS = ["ai_act_information", "mbl"] as const;
export type AttestationKind = (typeof ATTESTATION_KINDS)[number];
export const AttestationKindSchema = z.enum(ATTESTATION_KINDS);

/** Bump when the attested wording changes, so old records say what was shown. */
export const ATTESTATION_TEXT_VERSION: Record<AttestationKind, string> = {
  ai_act_information: "2026-10-v1",
  mbl: "2026-10-v1",
};

export const REQUIRED_ATTESTATIONS: Record<BusinessVertical, readonly AttestationKind[]> = {
  sales: ["ai_act_information"],
  dvh: ["ai_act_information"],
};

export const InviteAttestationRecordSchema = z.object({
  kind: AttestationKindSchema,
  /** ISO instant. */
  attestedAt: z.string(),
  attestedByUid: z.string().min(1),
  attestedByName: z.string().nullable(),
  textVersion: z.string().min(1),
});
export type InviteAttestationRecord = z.infer<typeof InviteAttestationRecordSchema>;

export const AttestInput = z.object({
  businessId: z.string().min(1),
  kinds: z.array(AttestationKindSchema).min(1),
});
export type AttestInput = z.infer<typeof AttestInput>;

/** The kinds a vertical requires that no record of the current text version covers. */
export function missingAttestations(
  vertical: BusinessVertical,
  records: readonly InviteAttestationRecord[] | null | undefined,
): AttestationKind[] {
  const have = new Set(
    (records ?? [])
      .filter((r) => r.textVersion === ATTESTATION_TEXT_VERSION[r.kind])
      .map((r) => r.kind),
  );
  return REQUIRED_ATTESTATIONS[vertical].filter((kind) => !have.has(kind));
}
